import { AsyncPipe, Location, NgClass } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { from } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import type { SwiperOptions } from "swiper/types";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import { InProgressAnimationComponent } from "../../../components/in-progress-animation/in-progress-animation.component";
import { GameDatasService } from "../services/game-data.service";
import { GamePlayService } from "../services/game-play.service";
import { GamePlayBaseComponent } from "../shared/game-play-base.component";

@Component({
    selector: "app-games-re-playable",
    templateUrl: "./games-re-playable.component.html",
    styleUrls: ["./games-re-playable.component.scss"],
    animations: [bounceIn],
    standalone: true,
    imports: [
        IonicModule,
        SwiperModule,
        NgClass,
        MatProgressSpinner,
        MatIcon,
        InProgressAnimationComponent,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class GamesRePayableComponent extends GamePlayBaseComponent implements OnInit {
  @ViewChild("progressSlides") progressSlides: SwiperComponent;

  activeSlideIndex = 0;
  speed: number;
  public languageCode: string;
  levelsSliderArr = [];
  public flashCost: Data;
  userGameRestriction: boolean;
  gameDetails$ = this.gameDatasService.getGameInfo(this.route.snapshot.params.id, "skill").pipe(
    map((res: any) => {
      try {
        this.authService.userDetails$?.pipe(takeUntil(this.destroy$)).subscribe(
          (user: Data) => {
            this.languageCode = user?.language_code ? user?.language_code : "en";
            this.translate.use(this.languageCode);
            this.preferredCurrency = user?.preferred_currency ?? CommonVariable.preferredCurrency;
            this.authService.getCurrencyList$?.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
              const CurrencyDetails = data?.details?.find((x: any) => x?.currency_code === this.preferredCurrency);
              this.currencySymbol =
                CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol;
            });
            this.userGameRestriction = user?.gameplay_restriction || false;
          },
          (err) => {
            return err;
          }
        );
        let skillGame = [];
        res.forEach((element) => {
          skillGame.push(element?.payload?.data());
        });
        let data = { ...skillGame[1], ...skillGame[0] };
        if (data && data.prize_details) {
          skillGame[1].prize_details.map((p, i) => {
            if (p?.prize_value?.int_prize) {
              this.authService
                .getDVFromString(p.prize_value.int_prize?.[this.languageCode])
                .then((resp: string) => {
                  data.prize_details[i].prize = resp;
                })
                .catch((err) => {
                  return err;
                });
            } else if (p?.prize_value?.prize) {
              this.authService
                .getDVFromString(p?.prize_value?.prize)
                .then((resp: string) => {
                  data.prize_details[i].prize = resp;
                })
                .catch((err) => {
                  return err;
                });
            }
          });
        }
        const totalPrize = data?.int_total_prize ? data.int_total_prize?.[this.languageCode] : data?.total_prize;
        if (totalPrize) {
          this.authService
            .getDVFromString(totalPrize)
            .then((resp: string) => {
              data.total_prize = resp;
            })
            .catch((err) => {
              return err;
            });
        }
        const selectedLanguageOrDefault = this.selectedLanguage || 'en';
        const gamePopupDetails = data?.int_learn_more_details
          ? data?.int_learn_more_details[selectedLanguageOrDefault]
          : data?.learn_more_details
          ? data?.learn_more_details
          : data?.int_game_popup_details
          ? data.int_game_popup_details?.[this.languageCode]
          : data?.game_popup_details;
        if (gamePopupDetails) {
          this.authService
            .getDVFromString(gamePopupDetails)
            .then((resp: string) => {
              data.game_popup_details = resp;
            })
            .catch((err) => {
              return err;
            });
        }

        const learn_more_rules = data?.int_learn_more_rules
          ? data?.int_learn_more_rules[selectedLanguageOrDefault]
          : data?.learn_more_rules;
        if (learn_more_rules) {
          this.authService
            .getDVFromString(learn_more_rules)
            .then((resp: string) => {
              data.learn_more_rules = resp;
            })
            .catch((err) => {
              return err;
            });
        }

        const prizeDetailsLength = data?.prize_details.length;
        if (prizeDetailsLength) {
          let achievedCount = 0;
          let inProgressCount = 0;
          from(data.prize_details)
            .pipe(map((val) => val))
            ?.subscribe((eachPrize: any) => {
              if (eachPrize?.is_achieved) {
                data.achieved_prize_count = achievedCount++;
              } else {
                data.inprogress_prize_count = inProgressCount++;
              }
            });
        }

        let firstSlide = data?.goal_details.filter((_, i) => i < 10);
        let secondSlide = data?.goal_details.filter((_, i) => i > 9);
        this.levelsSliderArr = [firstSlide, secondSlide];
        return data;
      } catch (e) {}
    })
  );
  public ratings: number[]; //  star ratings

  slidesOptions: SwiperOptions = {
    speed: 400,
    pagination: true,
    slidesPerView: 1,
  };

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    public translate: TranslateService,
    private GTMSegment: GTMSegmentService,
    private router: Router,
    private gameDatasService : GameDatasService,
  ) {
    super()
  }

  getPrizeAchieved(prize_details: any) {
    return prize_details.filter((p) => p.is_achieved);
  }

  getPrizeInProgress(prize_details: any) {
    return prize_details.filter((p) => !p.is_achieved);
  }

  ngOnInit() {
    super.ngOnInit();
    this.ratings = [3, 3, 3, 2, 3, 2, 0, -1];
    const navigation = this.router.getCurrentNavigation();
    this.flashCost = navigation?.extras.state;
  }

  async playGame(game: any) {
    this.isButtonDisabled = true;
    this.GTMSegment.HotJarEvent("user_played_game");
    game.document = "/api_post_request/customer_game_token_authentication";
    game.from_page = "re-playable";
    game.user_game_restriction = this.userGameRestriction;
    this.playGameBase(game);
  }


  async slidePrev(): Promise<void> {
    await this.progressSlides?.swiperRef?.slidePrev(this.speed);
    this.progressSlides?.swiperRef?.activeIndex;
    const acticeIndex = this.activeSlideIndex;
    if (acticeIndex === 0) {
      const prev = document.getElementById("slidePrev");
      if (!prev || !prev?.classList) return;
      prev?.classList?.add("activePrev");
      const next = document.getElementById("slideNext");
      if (!next || !next?.classList) return;
      next?.classList?.remove("activeNext");
    } else if (acticeIndex === 10) {
      const next = document.getElementById("slideNext");
      if (!next || !next?.classList) return;
      next?.classList?.add("activeNext");
      const prev = document.getElementById("slidePrev");
      if (!prev || !prev?.classList) return;
      prev?.classList?.remove("activePrev");
    } else if (acticeIndex >= 1) {
      const next = document.getElementById("slideNext");
      if (!next || !next?.classList) return;
      next?.classList?.add("activeNext");
      const prev = document.getElementById("slidePrev");
      if (!prev || !prev?.classList) return;
      prev?.classList?.remove("activePrev");
    }
  }

  async slideNext(): Promise<void> {
    await this.progressSlides?.swiperRef?.slideNext(this.speed);

    this.activeSlideIndex = await this.progressSlides?.swiperRef?.activeIndex;
    const acticeIndex = this.activeSlideIndex;

    if (acticeIndex === 10) {
      const next = document.getElementById("slideNext");
      if (!next || !next?.classList) return;
      next?.classList?.add("activeNext");
      const prev = document.getElementById("slidePrev");
      if (!prev || !prev?.classList) return;
      prev?.classList?.remove("activePrev");
    } else if (acticeIndex > 0 && acticeIndex !== 10) {
      const prev = document.getElementById("slidePrev");
      if (!prev || !prev?.classList) return;
      prev?.classList?.remove("activePrev");
      const next = document.getElementById("slideNext");
      if (!next || !next?.classList) return;
      next?.classList?.remove("activeNext");
    }
  }

  slidePrevNew() {
    this.progressSlides?.swiperRef?.slidePrev(this.speed);
  }
  slideNextNew() {
    this.progressSlides?.swiperRef?.slideNext(this.speed);
  }

  goBack() {
    this.location.back();
  }
}
